import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import type { Application } from '../../lib/supabase';
import './ApplicationsTable.css';

interface ApplicationWithJob extends Application {
  job_title?: string;
  job_poster?: string;
}

const ApplicationsTable: React.FC = () => {
  const [applications, setApplications] = useState<ApplicationWithJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; id: string; name: string } | null>(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50; // Load 50 applications at a time to reduce egress

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async (pageNum: number = 0) => {
    if (!supabase) {
      setError('Supabase not configured');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const start = pageNum * PAGE_SIZE;
      const end = start + PAGE_SIZE - 1;

      // Optimized query: only fetch necessary columns to reduce egress
      // Added pagination with range() to limit data transfer
      const { data, error: fetchError, count } = await supabase
        .from('applications')
        .select(`
          id,
          job_id,
          first_name,
          last_name,
          email,
          phone,
          cv_url,
          cover_letter,
          linkedin_url,
          status,
          created_at,
          jobs!inner (
            title_en,
            image_url
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(start, end);

      if (fetchError) throw fetchError;

      // Map applications with job details (bucket is public, use URLs directly)
      const applicationsWithJobTitle = data.map((app: any) => ({
        ...app,
        job_title: app.jobs?.title_en || 'Unknown Position',
        job_poster: app.jobs?.image_url || null,
      }));

      setApplications(applicationsWithJobTitle);
      setTotalCount(count || 0);
      setPage(pageNum);
    } catch (err: any) {
      console.error('Error fetching applications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, newStatus: Application['status']) => {
    if (!supabase) return;

    try {
      const { error: updateError } = await supabase
        .from('applications')
        .update({ status: newStatus })
        .eq('id', id);

      if (updateError) throw updateError;

      // Update local state
      setApplications(prev =>
        prev.map(app => (app.id === id ? { ...app, status: newStatus } : app))
      );
    } catch (err: any) {
      console.error('Error updating status:', err);
      alert(`Error updating status: ${err.message}`);
    }
  };

  const openDeleteModal = (id: string, applicantName: string) => {
    setDeleteModal({ show: true, id, name: applicantName });
  };

  const closeDeleteModal = () => {
    setDeleteModal(null);
  };

  const confirmDelete = async () => {
    if (!supabase || !deleteModal) return;

    try {
      const { error: deleteError } = await supabase
        .from('applications')
        .delete()
        .eq('id', deleteModal.id);

      if (deleteError) throw deleteError;

      // Update local state
      setApplications(prev => prev.filter(app => app.id !== deleteModal.id));
      closeDeleteModal();
    } catch (err: any) {
      console.error('Error deleting application:', err);
      alert(`Error deleting application: ${err.message}`);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeClass = (status: string) => {
    const statusMap: Record<string, string> = {
      new: 'status-new',
      reviewed: 'status-reviewed',
      shortlisted: 'status-shortlisted',
      rejected: 'status-rejected',
      hired: 'status-hired',
    };
    return statusMap[status] || 'status-new';
  };

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true;
    return app.status === filter;
  });

  const statusCounts = {
    all: applications.length,
    new: applications.filter(a => a.status === 'new').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    shortlisted: applications.filter(a => a.status === 'shortlisted').length,
  };

  if (loading) {
    return (
      <div className="applications-loading">
        <div className="spinner"></div>
        <p>Loading applications...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="applications-error">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10" strokeWidth="2"/>
          <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2"/>
          <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2"/>
        </svg>
        <h3>Error Loading Applications</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="applications-empty">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="8.5" cy="7" r="4"/>
          <polyline points="17 11 19 13 23 9"/>
        </svg>
        <h3>No Applications Yet</h3>
        <p>Applications will appear here when candidates apply for jobs</p>
      </div>
    );
  }

  return (
    <div className="applications-container">
      <div className="applications-header">
        <h2>Applications ({applications.length})</h2>
        <div className="applications-filters">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            All ({statusCounts.all})
          </button>
          <button
            className={filter === 'new' ? 'active' : ''}
            onClick={() => setFilter('new')}
          >
            New ({statusCounts.new})
          </button>
          <button
            className={filter === 'reviewed' ? 'active' : ''}
            onClick={() => setFilter('reviewed')}
          >
            Reviewed ({statusCounts.reviewed})
          </button>
          <button
            className={filter === 'shortlisted' ? 'active' : ''}
            onClick={() => setFilter('shortlisted')}
          >
            Shortlisted ({statusCounts.shortlisted})
          </button>
        </div>
      </div>

      <div className="applications-table-wrapper">
        <table className="applications-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Poster</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Position</th>
              <th>Applied</th>
              <th>Status</th>
              <th>CV</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApplications.map((app) => (
              <tr key={app.id}>
                <td className="applicant-name">
                  <div className="name-primary">{app.first_name} {app.last_name}</div>
                  {app.linkedin_url && (
                    <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="linkedin-link">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      LinkedIn
                    </a>
                  )}
                </td>
                <td className="poster-cell">
                  {app.job_poster ? (
                    <img
                      src={app.job_poster}
                      alt={`${app.job_title} poster`}
                      className="job-poster-thumbnail"
                    />
                  ) : (
                    <div className="no-poster">No poster</div>
                  )}
                </td>
                <td className="applicant-email">
                  <a href={`mailto:${app.email}`}>{app.email}</a>
                </td>
                <td>{app.phone}</td>
                <td className="job-title">{app.job_title}</td>
                <td className="date-cell">{formatDate(app.created_at!)}</td>
                <td>
                  <select
                    value={app.status || 'new'}
                    onChange={(e) => updateStatus(app.id!, e.target.value as Application['status'])}
                    className={`status-select ${getStatusBadgeClass(app.status || 'new')}`}
                  >
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="shortlisted">Shortlisted</option>
                    <option value="rejected">Rejected</option>
                    <option value="hired">Hired</option>
                  </select>
                </td>
                <td>
                  <a
                    href={app.cv_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cv-link"
                    title="Download CV"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="12" y1="18" x2="12" y2="12"/>
                      <line x1="9" y1="15" x2="12" y2="18"/>
                      <line x1="15" y1="15" x2="12" y2="18"/>
                    </svg>
                    View CV
                  </a>
                </td>
                <td>
                  <button
                    onClick={() => openDeleteModal(app.id!, `${app.first_name} ${app.last_name}`)}
                    className="delete-button"
                    title="Delete application"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      <line x1="10" y1="11" x2="10" y2="17"/>
                      <line x1="14" y1="11" x2="14" y2="17"/>
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalCount > PAGE_SIZE && (
        <div className="applications-pagination">
          <div className="pagination-info">
            Showing {page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
          </div>
          <div className="pagination-buttons">
            <button
              onClick={() => fetchApplications(page - 1)}
              disabled={page === 0 || loading}
              className="pagination-btn"
            >
              ← Previous
            </button>
            <span className="pagination-current">
              Page {page + 1} of {Math.ceil(totalCount / PAGE_SIZE)}
            </span>
            <button
              onClick={() => fetchApplications(page + 1)}
              disabled={(page + 1) * PAGE_SIZE >= totalCount || loading}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon-wrapper">
              <svg className="modal-warning-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>

            <h3 className="modal-title">⚠️ Catastrophic Action Warning</h3>

            <div className="modal-body">
              <p className="modal-main-text">
                You are about to <strong>permanently delete</strong> the application from <strong>{deleteModal.name}</strong>.
              </p>

              <div className="modal-warning-box">
                <p><strong>⚡ This action will:</strong></p>
                <ul>
                  <li>Permanently remove all applicant data from the system</li>
                  <li>Delete their CV and all associated files</li>
                  <li>Remove all application history</li>
                  <li><strong>Cannot be undone or recovered</strong></li>
                </ul>
              </div>

              <div className="modal-info-box">
                <p><strong>💡 Looking to reject an applicant?</strong></p>
                <p>
                  If you want to reject this applicant but keep their data for records,
                  please use the <strong>Status dropdown</strong> and set it to <strong>"Rejected"</strong> instead.
                  This delete button should only be used for removing spam, duplicate entries, or GDPR requests.
                </p>
              </div>

              <p className="modal-confirm-text">
                Are you absolutely sure you want to proceed with this permanent deletion?
              </p>
            </div>

            <div className="modal-actions">
              <button onClick={closeDeleteModal} className="modal-btn-cancel">
                Cancel
              </button>
              <button onClick={confirmDelete} className="modal-btn-delete">
                Yes, Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationsTable;
